import React, { useEffect, useState, useMemo } from "react";
import { v4 as uuidv4 } from "uuid";
import dayjs from "dayjs";

import useAuthScope from "hooks/useAuthScope";
import useDataSourceManager from "hooks/useDataSourceManager";

import { Box } from "@mui/material";
import AbnormalLocationAndAssestDisplacement from "./components/AbnormalLocationAndAssestDisplacement";
import RogueAPDetection from "./components/RogueAPDetection";
import DrawerField from "components/Drawer";

const getResponseData = (response) => {
  if (!response) return null;
  if (response?.result?.data) return response.result.data;
  if (response?.data) return response.data;
  return null;
};

const isSummaryNoData = (values = []) => {
  return values.every((item) => Number(item?.value) === 0);
};

const getRogueSummaryData = (data = {}) => [
  { label: "SSID Detected", value: data.rogueAp },
  { label: "Rogues SSIDs", value: data.highRiskAp },
  { label: "Impacted SSIDs", value: data.impactedSsid },
];

const getRogueChartData = (data = {}) => {
  if (!Array.isArray(data.series)) return [];

  return data.series.map(([time, value]) => ({
    label: dayjs(time).format("HH:mm"),
    value: value ?? 0,
  }));
};

const getAbnormalPieChartData = (data = {}) => [
  { name: "Secured", value: data.securedCount },
  { name: "Learning", value: data.learningCount },
  { name: "Displaced", value: data.displacedCount },
];

const isPieNoData = (pieData = []) => {
  const normalizedPieData = Array.isArray(pieData) ? pieData : [];

  return (
    normalizedPieData.length === 0 ||
    normalizedPieData.every((item) => Number(item?.value) === 0)
  );
};

const Security = () => {
  const [activeDrawer, setActiveDrawer] = useState(null);

  const { auth, isAuthReady, standalone } = useAuthScope();
  const { dataMap, getData, sendRequest } = useDataSourceManager();

  const [rogueUuid] = useState(uuidv4());
  const [abnormalUuid] = useState(uuidv4());
  const [rogueAPUuid] = useState(uuidv4());
  const [abnormalClientUuid] = useState(uuidv4());
  const [radioUuid] = useState(uuidv4());
  const [wirelessChannelInterfaceUuid] = useState(uuidv4());

  const [rogueData, setRogueData] = useState(getRogueSummaryData({}));
  const [chartRogueData, setChartRogueData] = useState(getRogueChartData({}));
  const [pieGroupsData, setPieGroupsData] = useState(
    getAbnormalPieChartData({}),
  );
  const [rogueAPData, setRogueAPData] = useState([]);
  const [abnormalClientData, setAbnormalClientData] = useState([]);
  const [radioData, setRadioData] = useState(null);

  const [wirelessChannelInterfaceApiData, setWirelessChannelInterfaceApiData,] = useState({});

  const [loadingMap, setLoadingMap] = useState({
    rogue: true,
    rogueAP: true,
    abnormal: true,
    abnormalClient: true,
    wirelessChannelInterface: true,
    radio: true,
  });

  const rogueNoData = useMemo(() => {
    return isSummaryNoData(rogueData) || isPieNoData(chartRogueData);
  }, [rogueData, chartRogueData]);

  const abnormalNoData = useMemo(() => {
    return isPieNoData(pieGroupsData);
  }, [pieGroupsData]);

  const handleCloseDrawer = () => {
    setActiveDrawer(null);
  };

  const buildRogueApiConfig = () => {
    if (standalone) {
      return {
        path: "/orgs/:orgId/sites/:siteId/security/rogueApDetection.read",
        params: {
          orgId: auth.realm?.id,
          siteId: auth.sites?.id,
        },
      };
    }

    return {
      path: "/ents/:entId/orgs/:orgId/sites/:siteId/security/rogueApDetection.read",
      params: {
        entId: auth.ents?.id,
        orgId: auth.orgs?.id,
        siteId: auth.sites?.id,
      },
    };
  };

  const buildRogueAPApiConfig = () => {
    if (standalone) {
      return {
        path: "/orgs/:orgId/sites/:siteId/security/rogueApDetection/apList.read",
        params: {
          orgId: auth.realm?.id,
          siteId: auth.sites?.id,
        },
      };
    }

    return {
      path: "/ents/:entId/orgs/:orgId/sites/:siteId/security/rogueApDetection/apList.read",
      params: {
        entId: auth.ents?.id,
        orgId: auth.orgs?.id,
        siteId: auth.sites?.id,
      },
    };
  };

  const buildAbnormalApiConfig = () => {
    if (standalone) {
      return {
        path: "/orgs/:orgId/sites/:siteId/security/abnormalLocation.read",
        params: {
          orgId: auth.realm?.id,
          siteId: auth.sites?.id,
        },
      };
    }

    return {
      path: "/ents/:entId/orgs/:orgId/sites/:siteId/security/abnormalLocation.read",
      params: {
        entId: auth.ents?.id,
        orgId: auth.orgs?.id,
        siteId: auth.sites?.id,
      },
    };
  };

  const buildAbnormalClientApiConfig = () => {
    if (standalone) {
      return {
        path: "/orgs/:orgId/sites/:siteId/security/abnormalLocation/clientList.read",
        params: {
          orgId: auth.realm?.id,
          siteId: auth.sites?.id,
        },
      };
    }

    return {
      path: "/ents/:entId/orgs/:orgId/sites/:siteId/security/abnormalLocation/clientList.read",
      params: {
        entId: auth.ents?.id,
        orgId: auth.orgs?.id,
        siteId: auth.sites?.id,
      },
    };
  };

  const buildWirelessChannelApiConfig = () => {
    if (standalone) {
      return {
        path: "/orgs/:orgId/sites/:siteId/linkIntegrity/channelInterference.read",
        params: {
          orgId: auth.realm?.id,
          siteId: auth.sites?.id,
        },
      };
    }

    return {
      path: "/ents/:entId/orgs/:orgId/sites/:siteId/linkIntegrity/channelInterference.read",
      params: {
        entId: auth.ents?.id,
        orgId: auth.orgs?.id,
        siteId: auth.sites?.id,
      },
    };
  }

  const buildRadioApiConfig = () => {
    if (standalone) {
      return {
        path: "/orgs/:orgId/sites/:siteId/settings/radio.read",
        params: {
          orgId: auth.realm?.id,
          siteId: auth.sites?.id,
        },
      };
    }

    return {
      path: "/ents/:entId/orgs/:orgId/sites/:siteId/settings/radio.read",
      params: {
        entId: auth.ents?.id,
        orgId: auth.orgs?.id,
        siteId: auth.sites?.id,
      },
    };
  }

  const refreshAbnormalLocationData = async () => {
    const abnormalApi = buildAbnormalApiConfig();
    const abnormalClientApi = buildAbnormalClientApiConfig();

    setLoadingMap((prev) => ({
      ...prev,
      abnormal: true,
      abnormalClient: true,
    }));

    sendRequest(abnormalApi.path, abnormalUuid, abnormalApi.params);

    await new Promise((resolve) => setTimeout(resolve, 300));

    sendRequest(
      abnormalClientApi.path,
      abnormalClientUuid,
      abnormalClientApi.params,
    );
  };

  useEffect(() => {
    if (!isAuthReady || !auth?.sites?.id) return;

    setLoadingMap({
      rogue: true,
      rogueAP: true,
      abnormal: true,
      abnormalClient: true,
      wirelessChannelInterface: true,
      radio: true
    });

    const requestList = [
      {
        uuid: rogueUuid,
        api: buildRogueApiConfig(),
      },
      {
        uuid: rogueAPUuid,
        api: buildRogueAPApiConfig(),
      },
      {
        uuid: abnormalUuid,
        api: buildAbnormalApiConfig(),
      },
      {
        uuid: abnormalClientUuid,
        api: buildAbnormalClientApiConfig(),
      },
      {
        uuid: wirelessChannelInterfaceUuid,
        api: buildWirelessChannelApiConfig(),
      },
      {
        uuid: radioUuid,
        api: buildRadioApiConfig(),
      },
    ];

    let cancelled = false;

    const sendRequestsSequentially = async () => {
      for (const { uuid, api } of requestList) {
        if (cancelled) return;

        sendRequest(api.path, uuid, api.params);

        await new Promise((resolve) => setTimeout(resolve, 300));
      }
    };

    sendRequestsSequentially();

    return () => {
      cancelled = true;
    };
  }, [
    isAuthReady,
    standalone,
    auth?.realm?.id,
    auth?.ents?.id,
    auth?.orgs?.id,
    auth?.sites?.id,
    rogueUuid,
    rogueAPUuid,
    abnormalUuid,
    abnormalClientUuid,
  ]);

  useEffect(() => {
    const rogueResponse = getData(rogueUuid);
    const data = getResponseData(rogueResponse);

    if (!data) return;

    setRogueData(getRogueSummaryData(data));
    setChartRogueData(getRogueChartData(data));
    setLoadingMap((prev) => ({ ...prev, rogue: false }));
  }, [dataMap, rogueUuid]);

  useEffect(() => {
    const rogueAPResponse = getData(rogueAPUuid);
    const data = getResponseData(rogueAPResponse)?.apList;

    if (!data) return;

    setRogueAPData(data);
    setLoadingMap((prev) => ({ ...prev, rogueAP: false }));
  }, [dataMap, rogueAPUuid]);

  useEffect(() => {
    const abnormalResponse = getData(abnormalUuid);
    const data = getResponseData(abnormalResponse);

    if (!data) return;

    setPieGroupsData(getAbnormalPieChartData(data));
    setLoadingMap((prev) => ({ ...prev, abnormal: false }));
  }, [dataMap, abnormalUuid]);

  useEffect(() => {
    const abnormalClientResponse = getData(abnormalClientUuid);
    const data = getResponseData(abnormalClientResponse)?.clientList;

    if (!data) return;

    setAbnormalClientData(data);
    setLoadingMap((prev) => ({ ...prev, abnormalClient: false }));
  }, [dataMap, abnormalClientUuid]);

  useEffect(() => {
    const wirelessChannelResponse = getData(wirelessChannelInterfaceUuid);
    const data = getResponseData(wirelessChannelResponse);

    if (!data) return;

    setWirelessChannelInterfaceApiData(data);
    setLoadingMap((prev) => ({ ...prev, wirelessChannelInterface: false }));
  }, [dataMap, wirelessChannelInterfaceUuid]);

  useEffect(() => {
    const radioResponse = getData(radioUuid);
    const data = getResponseData(radioResponse);

    if (!data) return;

    setRadioData(data);
    setLoadingMap((prev) => ({ ...prev, radio: false }));
  }, [dataMap, radioUuid]);

  return (
    <Box display="flex" gap={2}>
      <Box flex={1}>
        <RogueAPDetection
          loading={loadingMap.rogue || loadingMap.radio || loadingMap.wirelessChannelInterface}
          noData={rogueNoData}
          radioData={radioData}
          wirelessChannelInterfaceApiData={wirelessChannelInterfaceApiData}
          onClick={
            rogueNoData ? undefined : () => setActiveDrawer("rogue")
          }
          rogueData={rogueData}
          chartRogueData={chartRogueData}
        />
      </Box>

      <Box flex={1}>
        <AbnormalLocationAndAssestDisplacement
          loading={loadingMap.abnormal}
          noData={abnormalNoData}
          onClick={
            abnormalNoData ? undefined : () => setActiveDrawer("abnormal")
          }
          pieGroupsData={pieGroupsData}
        />
      </Box>

      {activeDrawer === "rogue" && (
        <DrawerField
          open
          handleClose={handleCloseDrawer}
          width="550px"
          contentKey="RogueAPDetection"
          commonContent="ainsightRogueAPDetectionDrawer"
          contentProps={{
            loading: loadingMap.rogueAP,
            noData: rogueNoData,
            chartData: chartRogueData,
            summaryData: rogueData,
            rogueAPData,
            auth,
          }}
        />
      )}

      {activeDrawer === "abnormal" && (
        <DrawerField
          open
          handleClose={handleCloseDrawer}
          width="750px"
          contentKey="AbnormalLocationAndAssestDisplacement"
          commonContent="ainsightAbnormalLocationAndAssestDisplacementDrawer"
          contentProps={{
            loading: loadingMap.abnormalClient,
            noData: abnormalNoData,
            chartData: pieGroupsData,
            abnormalClientData,
            auth,
            refreshAbnormalLocationData,
          }}
        />
      )}
    </Box>
  );
};

export default Security;
